import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, inject, Input, Output } from '@angular/core';
import { BaseServiceActionComponent } from '@app/move/components/base-service-action/base-service-action.component';
import { MoveSandbox } from '@app/move/sandboxes/move.sandbox';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonModule, Svg, SvgModule, UiContext } from '@smooved/ui';
import { PostHogEventsEnum, PosthogFrom, WaterMeterState } from '@smooved/core';
import { ServiceStatus } from '@app/services/enums/service-status.enum';
import { WaterFinalInvoiceService } from '@app/services/water-final-invoice/classes/water-final-invoice-service';
import { WaterFinalInvoiceServiceStore } from '@app/services/water-final-invoice/store/water-final-invoice-service.store';
import { UpdateServiceActiveComponent } from '@app/admin/components/update-service-active/update-service-active.component';
import { PosthogStatusServices } from '@app/tracking/enums/posthog.enum';
import { PosthogService } from '@app/tracking/posthog.service';
import { WaterCompositeStore } from '@app/services/water-composite/store/water-composite.store';
import { Move } from '@app/move/interfaces/move';
import { PosthogUtils } from '@app/tracking/utils/posthog.utils';

@Component({
    selector: 'app-water-final-invoice-service-submitted',
    template: `
        @if (waterFinalInvoiceService?.active) {
            <div [class.u-color-muted]="waterFinalInvoiceService?.isNotSubmitted()">
                @if (waterFinalInvoiceService?.isSubmitted()) {
                    <div class="u-flex-row u-flex-align-items-center">
                        <app-check-with-background [width]="20" [height]="20" class="u-margin-right-half"></app-check-with-background>
                        <span class="u-margin-right-half">{{
                            'ADMIN.DASHBOARD.DETAIL.WATER_FINAL_INVOICE_SERVICE.LABEL' | translate
                        }}</span>
                    </div>
                    <div class="u-margin-top-half u-margin-left-double">
                        <button smvd-ui-button (click)="updateServiceSubmitted(false)" [context]="uiContext.Alt">
                            {{ 'UNCHECK' | translate }}
                        </button>
                    </div>
                } @else {
                    <div class="u-flex-row u-flex-align-items-center">
                        <app-svg [name]="emptySvg" width="24" height="24" class="u-margin-right-half"></app-svg>
                        <span>{{ 'ADMIN.DASHBOARD.DETAIL.WATER_FINAL_INVOICE_SERVICE.LABEL' | translate }}</span>
                    </div>
                    <div class="u-margin-top-half u-margin-left-double">
                        <button smvd-ui-button id="check-button" (click)="updateServiceSubmitted(true)" [context]="uiContext.Alt">
                            {{ 'ADMIN.DASHBOARD.DETAIL.WATER_FINAL_INVOICE_SERVICE.CTA_CHECK' | translate }}
                        </button>
                    </div>
                }
            </div>
        } @else {
            <div class="u-flex-row u-flex-align-items-center">
                <app-svg [name]="Svg.Blocked" width="24" height="24" class="u-margin-right-half u-color-muted"></app-svg>
                <span class="u-color-muted">{{
                    'ADMIN.DASHBOARD.DETAIL.WATER_FINAL_INVOICE_SERVICE.LABEL_NOT_INTERESTED' | translate
                }}</span>
            </div>
        }

        <app-update-service-active
            class="u-margin-left-double u-display-block u-margin-top-half"
            [active]="waterFinalInvoiceService?.active"
            (update)="updateServiceActive($event)"
        ></app-update-service-active>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [SvgModule, TranslateModule, ButtonModule, UpdateServiceActiveComponent],
})
export class WaterFinalInvoiceServiceSubmittedComponent extends BaseServiceActionComponent {
    @Input() public waterFinalInvoiceService: WaterFinalInvoiceService;
    @Input() public isTransferee: boolean;
    @Output() public updated: EventEmitter<any> = new EventEmitter();

    public waterFinalInvoiceServiceStore = inject(WaterFinalInvoiceServiceStore);
    public waterCompositeStore = inject(WaterCompositeStore);

    constructor(
        protected readonly moveSandbox: MoveSandbox,
        protected readonly cdr: ChangeDetectorRef,
        protected readonly posthogService: PosthogService,
    ) {
        super(cdr, moveSandbox);
    }

    public readonly uiContext = UiContext;
    public readonly ServiceStatus = ServiceStatus;

    public async updateServiceActive(value: boolean): Promise<void> {
        if (this.waterFinalInvoiceService?.transactionId) {
            await this.waterFinalInvoiceServiceStore.updateActive(this.waterFinalInvoiceService.transactionId, value);
            this.posthogService.sendEventForTransaction(
                PostHogEventsEnum.SaveServiceHandling,
                this.waterFinalInvoiceService.transactionId,
                {
                    from: this.isTransferee ? PosthogFrom.AdminTransferee : PosthogFrom.AdminLeaver,
                    currentService: PosthogStatusServices.WaterTransfer,
                    newState: value
                        ? (this.waterCompositeStore.waterFinalInvoiceService()?.filingOption?.filingType ?? 'smooved (default)')
                        : 'No handling',
                    statusBeforeSave: PosthogUtils.getServiceStatusValue(this.waterFinalInvoiceServiceStore.service()?.status),
                },
            );

            this.updated.emit();
        }
    }

    public async updateServiceSubmitted(value: boolean): Promise<void> {
        if (this.waterFinalInvoiceService?.transactionId) {
            await this.waterFinalInvoiceServiceStore.submit(this.waterFinalInvoiceService.transactionId, value);
            this.updated.emit();
        }
    }

    protected readonly WaterMeterState = WaterMeterState;
    protected readonly Svg = Svg;
}
